import { useAppSelector } from "../../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../../services/hooks/useTranslationI18";
import { PAYMENT_METHOD, ReceiptItemStateEnum } from "../../../../utils/enums";
import { getFranchiseOrderType } from "../../../../utils/help/orderType/orderType";
import {
  DeliveryReceiptStatusEnum,
  PaidReceipt,
  RestaurantTypeEnum,
} from "../../../../utils/types";
import { selectCurrentDeliveryConfig } from "../../../../services/redux/selectors/deliverySelectors";

type UseDeliveryReceiptStateProps = {
  isOrdersTab: boolean;
  receipt: PaidReceipt;
};

export const useDeliveryReceiptState = (
  props: UseDeliveryReceiptStateProps
) => {
  const { isOrdersTab, receipt } = props;
  const { t } = useTranslationI18();

  const restaurantConfig = useAppSelector(
    (state) => state.config.restaurantConfig
  );

  // Always call unconditionally to satisfy Rules of Hooks;
  // select the right source based on isOrdersTab.
  const deliveryReceiptFromRedux = useAppSelector(
    (state) => state.receipt.deliveryReceipt
  );
  const deliveryReceipt = isOrdersTab
    ? receipt.deliveryReceipt
    : deliveryReceiptFromRedux;

  const restaurantType = useAppSelector(
    (state) => state.config?.restaurantInfo?.type
  );
  const isEcommerce = restaurantType === RestaurantTypeEnum.ECOMMERCE;

  const franchiseConfig = useAppSelector(
    (state) => state.config.selectedFranchise?.franchiseConfig
  );
  const currentFranchiseDeliveryConfg = useAppSelector(
    selectCurrentDeliveryConfig
  );
  const orderTypeParam = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );
  const franchiseOrderTypeConfig = getFranchiseOrderType(
    franchiseConfig,
    currentFranchiseDeliveryConfg
  );

  const pendingText = t("pay:pending-text");
  const confirmedText = t("pay:confirmed-text");
  const addressTo = t("delivery:address-to");
  const pendingPayment = t("pay:pending-payment");
  const onItsWay = t("delivery:on-its-way");
  const delivered = t("delivery:delivered");
  const prepOnPayText = t("pay:prep-on-pay");
  const paymentProcessing = t("pay:payment-processing");
  const preparingOrder = t("pay:we-are-preparing-your-order");
  const restaurantIsPreparingOrder = t("pay:restaurant-preparing-your-order");

  const workingOnYourOder = t("pay:preparing");

  const confirmedShortly = t(
    isEcommerce ? "pay:confirmed-shortly-onlineStore" : "pay:confirmed-shortly"
  );
  const driverOnWayToBranch = t("delivery:driver-on-way-to-branch");
  const driverOnWayToDelivery = t("delivery:driver-heading-your-way");
  const enjoyYourFood = t("common:enjoy-your-food");
  // Delivery orders stay on the preparing step until something reports the
  // order leaving the branch, so a COMPLETED receipt with no dispatched
  // delivery status reads the same as a CONFIRMED one (prepits/project#4143).
  const preparingStateText = isEcommerce
    ? preparingOrder
    : restaurantIsPreparingOrder + ". " + driverOnWayToBranch;
  const orderCancelledI18 = t("pay:order-cancelled");

  const deliveryReceiptStateText = () => {
    const orderTypePayment = franchiseOrderTypeConfig[orderTypeParam || ""];

    if (
      receipt.state === ReceiptItemStateEnum.CANCELLED ||
      deliveryReceipt?.deliveryStatus === DeliveryReceiptStatusEnum.CANCELLED
    ) {
      return orderCancelledI18;
    }

    if (deliveryReceipt?.deliveryStatus) {
      switch (deliveryReceipt.deliveryStatus) {
        case DeliveryReceiptStatusEnum.ON_WAY_TO_DELIVERY:
          return driverOnWayToDelivery;
        case DeliveryReceiptStatusEnum.DELIVERED:
          return enjoyYourFood;
        case DeliveryReceiptStatusEnum.PENDING:
        default:
          if (receipt.state === ReceiptItemStateEnum.PENDING) {
            if (orderTypePayment?.prepOnPay) {
              return prepOnPayText;
            }
            if (
              receipt.paymentMethod === PAYMENT_METHOD.ONLINE_CARD &&
              !receipt.paid_at
            ) {
              return paymentProcessing;
            }

            return confirmedShortly;
          } else if (
            receipt.state === ReceiptItemStateEnum.CONFIRMED ||
            receipt.state === ReceiptItemStateEnum.COMPLETED
          ) {
            // COMPLETED only means the kitchen is done. Until the delivery
            // status reports the order leaving the branch the guest is still
            // waiting on the same step, and leaving this out left the screen
            // with no subtitle at all.
            return preparingStateText;
          }
          break;
      }
    } else if (
      receipt.state === ReceiptItemStateEnum.CONFIRMED ||
      receipt.state === ReceiptItemStateEnum.COMPLETED
    ) {
      return preparingStateText;
    } else if (receipt.state === ReceiptItemStateEnum.PENDING) {
      if (orderTypePayment?.prepOnPay) {
        return prepOnPayText;
      }
      if (
        receipt.paymentMethod === PAYMENT_METHOD.ONLINE_CARD &&
        !receipt.paid_at
      ) {
        return paymentProcessing;
      }

      return confirmedShortly;
    }
  };

  const showDeliveryAddress =
    !isEcommerce ||
    (isEcommerce && receipt.state === ReceiptItemStateEnum.CONFIRMED);

  const deliveryAddressText = receipt.note
    ? receipt.note
    : `${receipt.deliveryReceipt?.address?.addressTitle}, ${receipt.deliveryReceipt?.address?.streetName}`;

  const progressClass = () => {
    if (
      receipt.state === ReceiptItemStateEnum.PENDING ||
      deliveryReceipt?.deliveryStatus === DeliveryReceiptStatusEnum.PENDING
    ) {
      return "pending";
    }
    if (
      deliveryReceipt?.deliveryStatus === DeliveryReceiptStatusEnum.DELIVERED
    ) {
      return "ready";
    }
    if (
      deliveryReceipt?.deliveryStatus ===
      DeliveryReceiptStatusEnum.ON_WAY_TO_DELIVERY
    ) {
      return "on_the_way";
    }

    return "preparing";
  };

  const secondStepActive =
    receipt.state === ReceiptItemStateEnum.CONFIRMED ||
    receipt.state === ReceiptItemStateEnum.COMPLETED ||
    deliveryReceipt?.deliveryStatus ===
      DeliveryReceiptStatusEnum.ON_WAY_TO_PICKUP;

  const thirdStepActive =
    deliveryReceipt?.deliveryStatus ===
      DeliveryReceiptStatusEnum.ON_WAY_TO_DELIVERY ||
    deliveryReceipt?.deliveryStatus === DeliveryReceiptStatusEnum.DELIVERED;

  const fourthStepActive =
    deliveryReceipt?.deliveryStatus === DeliveryReceiptStatusEnum.DELIVERED;

  const secondStepLabel = !isEcommerce
    ? workingOnYourOder
    : restaurantConfig?.settleBilleAndCompleteInOne &&
      receipt.paymentMethod === PAYMENT_METHOD.CASH
    ? pendingPayment
    : confirmedText;

  const isCancelled = receipt.state === ReceiptItemStateEnum.CANCELLED;

  return {
    t,
    isOrdersTab,
    isEcommerce,
    receipt,
    deliveryReceipt,
    deliveryReceiptStateText,
    addressTo,
    showDeliveryAddress,
    deliveryAddressText,
    progressClass,
    pendingText,
    secondStepLabel,
    secondStepActive,
    thirdStepActive,
    fourthStepActive,
    onItsWay,
    delivered,
    isCancelled,
    franchiseConfig,
  };
};
